import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../components/Head/SeoTags";
import EncryptionMock from "../../components/Mockups/EncryptionMock";

const APP_URL = process.env.REACT_APP_APP_URL;

const PRACTICES = [
  { icon: "bi-cpu", key: "clientSide" },
  { icon: "bi-key", key: "pbkdf2" },
  { icon: "bi-eye-slash", key: "zeroKnowledge" },
  { icon: "bi-shield-lock", key: "bcrypt" },
  { icon: "bi-globe2", key: "publicPages" },
  { icon: "bi-box-arrow-down", key: "exports" },
];

const BOUNDARIES = ["selective", "noRecovery", "formatUpgrades"];

const Security = () => {
  const { t } = useTranslation();

  return (
    <>
      <SeoTags
        title={t("security.seo.title")}
        description={t("security.seo.description")}
        keywords={t("security.seo.keywords")}
        path="/security"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t("security.schema.name"),
            description: t("security.schema.description"),
            url: "https://onbookmarks.com/security",
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: t("security.schema.home"),
                  item: "https://onbookmarks.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: t("security.schema.security"),
                  item: "https://onbookmarks.com/security",
                },
              ],
            },
          }),
        }}
      />

      {/* Hero */}
      <section className="hero bg-purple-subtle">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-6">
              <div className="d-flex flex-column gap-4">
                <div>
                  <span className="badge bg-purple text-white px-3 py-2">
                    <i className="bi bi-shield-lock me-2"></i>
                    {t("security.hero.badge")}
                  </span>
                </div>
                <h1 className="display-4 fw-bold">{t("security.hero.title")}</h1>
                <p className="lead">{t("security.hero.description")}</p>
                <div className="d-flex gap-3 flex-wrap">
                  <Link
                    to={`${APP_URL}/register`}
                    className="btn btn-primary btn-lg px-4"
                  >
                    {t("security.hero.ctaPrimary")}
                  </Link>
                  <Link
                    to="/features/encryption"
                    className="btn btn-outline-primary btn-lg px-4"
                  >
                    {t("security.hero.ctaSecondary")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
              <EncryptionMock />
            </div>
          </div>
        </div>
      </section>

      {/* Practices grid */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="h1 fw-bold mb-4">{t("security.practices.title")}</h2>
              <p className="lead text-muted">
                {t("security.practices.subtitle")}
              </p>
            </div>
          </div>
          <div className="row g-4">
            {PRACTICES.map((p) => (
              <div key={p.key} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="icon icon-lg bg-purple-subtle rounded-3 mb-3">
                      <i className={`bi ${p.icon} fs-4 text-purple`}></i>
                    </div>
                    <h3 className="h5 mb-2">
                      {t(`security.practices.${p.key}.title`)}
                    </h3>
                    <p className="text-muted mb-0">
                      {t(`security.practices.${p.key}.text`)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How encryption works */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold mb-4">{t("security.how.title")}</h2>
            <p className="lead text-muted">{t("security.how.subtitle")}</p>
          </div>
          <div className="row g-4">
            {["step1", "step2", "step3"].map((step, i) => (
              <div className="col-md-4" key={step}>
                <div className="text-center">
                  <div
                    className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "80px", height: "80px" }}
                  >
                    <span className="fs-2 fw-bold text-purple">{i + 1}</span>
                  </div>
                  <h3 className="h5 mb-3">{t(`security.how.${step}.title`)}</h3>
                  <p className="text-muted">{t(`security.how.${step}.text`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Honest boundaries */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <h2 className="h1 fw-bold mb-4 text-center">
                {t("security.boundaries.title")}
              </h2>
              <p className="lead text-muted text-center mb-5">
                {t("security.boundaries.subtitle")}
              </p>
              {BOUNDARIES.map((b) => (
                <div key={b} className="d-flex align-items-start mb-4">
                  <i className="bi bi-info-circle-fill text-purple fs-5 me-3 mt-1"></i>
                  <div>
                    <h3 className="h6 mb-1">
                      {t(`security.boundaries.${b}.title`)}
                    </h3>
                    <p className="text-muted mb-0">
                      {t(`security.boundaries.${b}.text`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Disclosure */}
      <section className="py-4">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="card border-0 bg-purple-subtle">
                <div className="card-body p-4 d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="icon icon-lg bg-white rounded-3">
                      <i className="bi bi-bug fs-4 text-purple"></i>
                    </div>
                    <div>
                      <h4 className="h6 mb-1">{t("security.disclosure.title")}</h4>
                      <p className="text-muted mb-0 small">
                        {t("security.disclosure.text")}
                      </p>
                    </div>
                  </div>
                  <a
                    href="mailto:support@onbookmarks.com?subject=Security%20report"
                    className="btn btn-purple btn-sm text-nowrap"
                  >
                    {t("security.disclosure.cta")}
                    <i className="bi bi-arrow-right ms-2"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-5">
        <div className="container">
          <div className="bg-purple rounded-4 p-5 text-center">
            <h2 className="h1 fw-bold mb-4 text-white">{t("security.cta.title")}</h2>
            <p className="lead mb-4 text-white">{t("security.cta.description")}</p>
            <Link to={`${APP_URL}/register`} className="btn btn-light btn-lg px-5">
              {t("security.cta.button")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Security;
